import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import type { Booking, PaymentStatus } from "../../api/bookings";
import type { BookingModal } from "../../store/bookingsSlice";
import { BookingActionsMenu } from "./BookingActionsMenu";
import { CalendarSyncWarning } from "./CalendarSyncWarning";
import { PaymentStatusBadge } from "./PaymentStatusBadge";

interface BookingsTableProps {
  bookings: Booking[];
  loading: boolean;
  onAction: (action: BookingModal, booking: Booking) => void;
  paymentStatusFilter?: PaymentStatus | "";
  onPaymentStatusChange?: (value: PaymentStatus | "") => void;
  onRowClick?: (booking: Booking) => void;
}

export const BookingsTable: React.FC<BookingsTableProps> = ({
  bookings,
  loading,
  onAction,
  onRowClick,
}) => {
  const showPaymentColumn = bookings.some((b) => b.payment_required);
  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ py: 4 }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (bookings.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{ py: 4 }}
      >
        No bookings found.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "grey.100" }}>
            <TableCell sx={{ fontWeight: "bold" }}>Date / Time</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Service</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Duration</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Customer</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
            {showPaymentColumn && (
              <TableCell sx={{ fontWeight: "bold" }}>Payment</TableCell>
            )}
            <TableCell sx={{ fontWeight: "bold" }}>Notes</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {bookings.map((booking) => (
            <TableRow
              key={booking.booking_id}
              hover
              onClick={onRowClick ? () => onRowClick(booking) : undefined}
              sx={onRowClick ? { cursor: "pointer" } : undefined}
            >
              <TableCell>
                {format(new Date(booking.preferred_date_time), "dd MMM yyyy HH:mm")}
                {!booking.calendar_sync_available && booking.calendar_event_id && (
                  <CalendarSyncWarning provider={booking.provider} />
                )}
              </TableCell>
              <TableCell>{booking.service_name}</TableCell>
              <TableCell>{booking.duration_minutes} mins</TableCell>
              <TableCell>
                <Typography variant="body2">{booking.customer_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {booking.customer_email}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={booking.status}
                  size="small"
                  color={booking.status === "confirmed" ? "success" : "default"}
                  variant="outlined"
                />
              </TableCell>
              {showPaymentColumn && (
                <TableCell>
                  <PaymentStatusBadge status={booking.payment_status} />
                </TableCell>
              )}
              <TableCell>{booking.notes || "—"}</TableCell>
              <TableCell
                align="right"
                onClick={(e) => e.stopPropagation()}
              >
                <BookingActionsMenu booking={booking} onAction={onAction} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
